import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';
import { readDate, writeDate } from '../../utils/appDate';

/**
 * Every call the vendors book makes.
 *
 * The mirror of `customersApi`, and the API mirrors it too: the transaction
 * routes take a `vendor` exactly where the customer ones take a `customer`,
 * and `createTransactionFn` branches on whichever it was given. So the two
 * books are the same machine pointed in opposite directions.
 *
 * ── The direction ───────────────────────────────────────────────────────────
 *
 * `computeBalanceDelta` on the server is the whole story, and it is the same
 * for both:
 *
 *     debit  → balance UP
 *     credit → balance DOWN
 *
 * For a CUSTOMER, balance is what they owe the shop. For a VENDOR it is what
 * the SHOP owes them. So the arithmetic is identical and only the sentence
 * changes: a debit here is a new bill from the supplier, and a credit is the
 * shop paying it down.
 *
 * This is also where the expenses screen's invoiced expenses end up — each one
 * writes a `debit` against its vendor — which is why those entries appear in
 * this ledger without anybody typing them.
 *
 * Restricted server-side to manager, cashier, accountant and superadmin.
 */

const auth = (token) => ({ headers: { authorization: token } });

export function fetchVendors({ businessId, token }) {
  return axios
    .get(`${constants.baseUrl}/vendors/get-business-vendors/${businessId}`, auth(token))
    .then((res) => res.data?.data?.vendors || []);
}

/** Note the singular key: create and update answer with `vendor`, not `vendors`. */
export function createVendor({ payload, token }) {
  return axios
    .post(`${constants.baseUrl}/vendors`, payload, auth(token))
    .then((res) => res.data?.data?.vendor);
}

export function updateVendor({ id, payload, token }) {
  return axios
    .patch(`${constants.baseUrl}/vendors/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.vendor);
}

export function fetchTransactions({ vendorId, token }) {
  return axios
    .get(`${constants.baseUrl}/transactions/get-vendor-transactions/${vendorId}`, auth(token))
    .then((res) => res.data?.data?.transactions || []);
}

/**
 * One entry in a vendor's book.
 *
 *   `debit`  — Deen Cusub, a new bill. What the shop owes goes UP.
 *   `credit` — Bixin, money paid to them. It goes DOWN.
 *
 * `business` is deliberately not sent: `createTransactionFn` fills it from the
 * vendor, and one source is better than two that can disagree.
 *
 * `transactionType: 'custom'` separates a hand-entered line from the ones an
 * expense writes by itself.
 */
export function createTransaction({ vendorId, userId, kind, amount, description, date, token }) {
  const value = Number(amount);

  const payload = {
    vendor: vendorId,
    user: userId,
    transactionType: 'custom',
    description,
    date: writeDate(date),
    debit: kind === 'charge' ? value : 0,
    credit: kind === 'payment' ? value : 0,
  };

  return axios
    .post(`${constants.baseUrl}/transactions`, payload, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * Correct a line already in the book.
 *
 * Which side it sits on is not editable — turning a bill into a payment is a
 * different entry, not a correction, and doing it by accident moves the
 * balance by twice the amount. The server applies the difference, so a bill
 * corrected from $50 to $40 moves the balance by $10.
 */
export function updateTransaction({ id, amount, description, date, kind, token }) {
  const value = Number(amount);

  const payload = {
    description,
    date: writeDate(date),
    debit: kind === 'charge' ? value : 0,
    credit: kind === 'payment' ? value : 0,
  };

  return axios
    .patch(`${constants.baseUrl}/transactions/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * Remove a line. A soft delete on the server: the row is kept but cut loose
 * and the balance it moved is put back.
 */
export function deleteTransaction({ id, token }) {
  return axios
    .delete(`${constants.baseUrl}/transactions/${id}`, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * The running balance, oldest first.
 *
 * The server stores each line but not the balance after it, so the column is
 * derived here. Every date goes through `readDate` first — the lines an
 * expense wrote and the lines this screen wrote are the same wall-clock
 * fiction, and sorting raw values would interleave them wrongly.
 */
export function withRunningBalance(transactions) {
  const sorted = (transactions || [])
    .slice()
    .sort((a, b) => readDate(a.date).valueOf() - readDate(b.date).valueOf());

  let running = 0;
  return sorted.map((transaction) => {
    running += Number(transaction.debit || 0) - Number(transaction.credit || 0);
    return {
      ...transaction,
      // Rounded at each step, not at the end: a book that shows 46.00 and
      // 71.50 must not total 117.49999999999999.
      balance: Math.round(running * 100) / 100,
    };
  });
}
